import React, { useMemo } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import type { Chord } from '@/types';
import { getScaleDegreeColor, CHROMATIC_INDICATORS } from '@/styles/colors';
import { getChordBadgeText, hasChordModifications } from '@/utils/chord-helpers';
import { useAccessibilityStore } from '@/store/accessibility-store';
import { getPatternId } from '@/styles/patterns';
import { ChromaticBadge } from './ChromaticBadge';
import { UncertaintyBadge } from './UncertaintyBadge';
import styles from './ChordShape.module.css';

interface ChordShapeProps {
  chord: Chord;
  isSelected?: boolean;
  isPlaying?: boolean;
  isDragging?: boolean;
  /** When true, renders at reduced opacity to indicate a chord has already been played back */
  isPast?: boolean;
  hasAnnotation?: boolean;
  onSelect?: () => void;
  onHover?: () => void;
  zoom?: number;
  /** Callback when uncertainty badge is clicked (GRE-100) */
  onUncertaintyClick?: () => void;
  /**
   * When provided, renders a small label above the shape in Fraunces italic.
   * Intended for chord name or root note display. Callers opt in; omitting
   * the prop (the default) renders nothing.
   */
  nameAbove?: string;
  /**
   * When provided, renders a small label below the shape in DM Mono.
   * Intended for a secondary Roman numeral or functional label.
   * Callers opt in; omitting the prop renders nothing.
   */
  romanBelow?: string;
}

// Wobble configuration for organic feel
const WOBBLE_CONFIG = {
  rotation: 1.5,   // ±1.5° rotation
  scale: 0.02,     // ±2% scale
} as const;

/**
 * Fieldbook function wash (foundation §4). Hue is double-coded with shape:
 * tonic gold, motion teal, tension orange, borrowed plum. Chromatic chords
 * read as borrowed, except secondary dominants which keep the tension hue
 * (they still function as dominants).
 *
 * Exported for reuse by SnippetsPanel's mini shape-strip preview, which
 * needs the same function->wash mapping for SnippetChords (not full Chords).
 */
export function getFunctionWashVar(chord: Pick<Chord, 'isChromatic' | 'chromaticType' | 'scaleDegree'>): string {
  if (chord.isChromatic) {
    return chord.chromaticType === 'secondary'
      ? 'var(--wash-tension)'
      : 'var(--wash-borrowed)';
  }
  switch (chord.scaleDegree) {
    case 1:
    case 6:
      return 'var(--wash-tonic)';   // tonic function
    case 2:
    case 4:
      return 'var(--wash-motion)';  // predominant / motion
    case 5:
    case 7:
      return 'var(--wash-tension)'; // dominant / tension
    case 3:
    default:
      return 'var(--wash-borrowed)'; // mediant reads as the odd degree out
  }
}

/** Primary-function chords (I, IV, V) carry the heaviest structural weight. */
function isPrimaryFunction(scaleDegree: number): boolean {
  return scaleDegree === 1 || scaleDegree === 4 || scaleDegree === 5;
}


const ChordShapeComponent: React.FC<ChordShapeProps> = ({
  chord,
  isSelected = false,
  isPlaying = false,
  isDragging = false,
  isPast = false,
  hasAnnotation = false,
  onSelect,
  onHover,
  zoom = 1.0,
  onUncertaintyClick,
  nameAbove,
  romanBelow,
}) => {
  const size = chord.size * zoom;
  const colorblindMode = useAccessibilityStore((state) => state.colorblindMode);
  const prefersReducedMotion = useReducedMotion();

  const shapePath = useMemo(() => {
    return generateShapePath(chord.scaleDegree, size);
  }, [chord.scaleDegree, size]);

  // Seeded pseudo-randomness from the chord ID — same chord always renders the
  // same wobble and echo-stroke angle (deterministic, stable across renders).
  const seededRandom = useMemo(() => {
    const seed = chord.id.split('').reduce((a, b) => a + b.charCodeAt(0), 0);
    return (index: number) => {
      const x = Math.sin(seed + index) * 10000;
      return x - Math.floor(x);
    };
  }, [chord.id]);

  // Generate stable organic transform based on chord ID
  const organicTransform = useMemo(() => {
    const rotation = (seededRandom(1) - 0.5) * WOBBLE_CONFIG.rotation;
    const scale = 1 + (seededRandom(2) - 0.5) * WOBBLE_CONFIG.scale;
    return `rotate(${rotation}deg) scale(${scale})`;
  }, [seededRandom]);

  // Fieldbook fill: a translucent function wash. Hue is double-coded with the
  // shape (foundation §4); intensity encodes structural weight (§1 opacity
  // scale). Primary-function chords (I, IV, V) read at --wash-strong; softer
  // relatives, inversions, and chromatic chords at --wash-mid. Selection and
  // playback bloom the wash (+0.12 / +0.10).
  const washHue = getFunctionWashVar(chord);
  const baseWashOpacity = isPrimaryFunction(chord.scaleDegree)
    ? 0.50 // --wash-strong
    : 0.30; // --wash-mid
  const washOpacity =
    baseWashOpacity + (isSelected ? 0.12 : 0) + (isPlaying ? 0.10 : 0);

  // Deterministic echo-stroke angle (1.5–4° off true, signed) — the "softer
  // echo" pen line that appears ONLY on chord shapes (foundation §3).
  const echoAngle =
    (1.5 + seededRandom(3) * 2.5) * (seededRandom(4) < 0.5 ? -1 : 1);
  const shapeCenter = size / 2;

  // Fieldbook motion (§5): no glows, nothing springs. Selection is carried by
  // the vermilion ring, not a bloom of light. The playing chord blooms with a
  // small scale settle (1.03); the wash brightening is handled via opacity.
  const variants = {
    default: {
      scale: 1.0,
      filter: 'drop-shadow(0 1px 2px rgba(70,55,35,0.08))',
    },
    hover: {
      scale: 1.04,
      filter: 'drop-shadow(0 2px 5px rgba(70,55,35,0.12))',
    },
    selected: {
      scale: 1.0,
      filter: 'drop-shadow(0 1px 2px rgba(70,55,35,0.08))',
    },
    playing: {
      scale: 1.03,
      filter: 'drop-shadow(0 1px 2px rgba(70,55,35,0.08))',
    },
    dragging: {
      scale: 1.08,
      filter: 'drop-shadow(0 6px 18px rgba(70,55,35,0.12))',
    },
  };

  const currentState = isDragging
    ? 'dragging'
    : isPlaying
    ? 'playing'
    : isSelected
    ? 'selected'
    : 'default';

  // Roman numeral label rendered inside the shape
  const ROMAN_NUMERALS_MAJOR: Record<number, string> = {
    1: 'I', 2: 'ii', 3: 'iii', 4: 'IV', 5: 'V', 6: 'vi', 7: 'vii°',
  };
  const ROMAN_NUMERALS_MINOR: Record<number, string> = {
    1: 'i', 2: 'ii°', 3: 'III', 4: 'iv', 5: 'V', 6: 'VI', 7: 'VII',
  };
  const romanNumeralLabel = chord.mode === 'major'
    ? (ROMAN_NUMERALS_MAJOR[chord.scaleDegree] ?? '?')
    : (ROMAN_NUMERALS_MINOR[chord.scaleDegree] ?? '?');

  const innerLabel = romanNumeralLabel;
  // Numerals are pen-written in ink over the translucent wash (foundation §2).
  const innerLabelColor = 'var(--ink)';

  // Font size scales slightly with shape size; clamp between 12 and 16px
  const labelFontSize = Math.min(16, Math.max(12, size * 0.28));

  // Main chord shape content
  const shapeContent = (
    <motion.div
      className={styles.chordShapeContainer}
      initial="default"
      animate={currentState}
      whileHover="hover"
      variants={variants}
      transition={
        prefersReducedMotion
          ? { duration: 0.12 }
          : {
              // Playing chord blooms and settles in 120ms (foundation §5);
              // no infinite pulse, nothing springs.
              duration: isPlaying ? 0.12 : 0.3,
              ease: [0.4, 0.0, 0.2, 1],
            }
      }
      onClick={onSelect}
      onMouseEnter={onHover}
      role="button"
      aria-label={`${chord.quality} chord on scale degree ${chord.scaleDegree}`}
      tabIndex={0}
      style={{ transform: organicTransform, opacity: isPast ? 0.4 : 1 }}
    >
      {nameAbove && (
        <div className={styles.nameAbove}>{nameAbove}</div>
      )}
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        xmlns="http://www.w3.org/2000/svg"
      >
        {(chord.isChromatic || colorblindMode) && (
          <defs>
            {chord.isChromatic && (
              <linearGradient id={`shimmer-${chord.id}`} x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor={CHROMATIC_INDICATORS.shimmerGradientStart} stopOpacity={CHROMATIC_INDICATORS.shimmerOpacity} />
                <stop offset="100%" stopColor={CHROMATIC_INDICATORS.shimmerGradientEnd} stopOpacity={CHROMATIC_INDICATORS.shimmerOpacity} />
              </linearGradient>
            )}
          </defs>
        )}

        {/* Echo stroke — a softer second pen line rotated slightly off true.
            Sits behind the main shape so it peeks out as a doubled ink line.
            Fieldbook reserves this seasoning for chord shapes alone (§3). */}
        <path
          d={shapePath}
          fill="none"
          stroke="var(--ink)"
          strokeWidth={0.8}
          opacity={0.5}
          transform={`rotate(${echoAngle} ${shapeCenter} ${shapeCenter})`}
          pointerEvents="none"
        />

        {/* Main shape — translucent function wash filled, drawn with a 1.6px
            ink pen line. */}
        <path
          d={shapePath}
          fill={washHue}
          fillOpacity={washOpacity}
          stroke="var(--ink)"
          strokeWidth={1.6}
          className={styles.chordShape}
        />

        {colorblindMode && chord.scaleDegree !== 1 && (
          <path
            d={shapePath}
            fill={`url(#${getPatternId(chord.scaleDegree)})`}
            pointerEvents="none"
          />
        )}

        {chord.isChromatic && (
          <>
            <path
              d={shapePath}
              fill={`url(#shimmer-${chord.id})`}
              className={styles.chromaticShimmer}
            />
            <path
              d={shapePath}
              fill="none"
              stroke={CHROMATIC_INDICATORS.edgeGlow}
              strokeWidth={1}
              opacity={CHROMATIC_INDICATORS.glowOpacity}
            />
          </>
        )}

        {/* Selection treatment — a solid vermilion ring circled just outside
            the shape, like a red-pen mark (foundation §3). Solid, not dashed:
            dashes are reserved for "not yet real". */}
        {isSelected && (
          <path
            d={shapePath}
            fill="none"
            stroke="var(--vermilion)"
            strokeWidth={1.5}
            transform={`translate(${shapeCenter} ${shapeCenter}) scale(1.09) translate(${-shapeCenter} ${-shapeCenter})`}
            pointerEvents="none"
          />
        )}

        {/* Roman numeral label centered inside shape — Fraunces, pen-written */}
        <text
          x={size / 2}
          y={size / 2}
          textAnchor="middle"
          dominantBaseline="central"
          className={styles.innerLabel}
          style={{
            fontSize: labelFontSize,
            fill: innerLabelColor,
            fontFamily: 'var(--font-display)',
            fontStyle: 'normal',
            fontWeight: 500,
            pointerEvents: 'none',
            userSelect: 'none',
          }}
        >
          {innerLabel}
        </text>
      </svg>

      {hasChordModifications(chord.quality, chord.extensions, chord.scaleDegree, chord.mode) && (() => {
        const badgeText = getChordBadgeText(chord.quality, chord.extensions, chord.scaleDegree, chord.mode);
        if (!badgeText) return null;

        const badgeColor = getScaleDegreeColor(chord.scaleDegree, chord.mode);
        const borderColor = `rgba(${hexToRgb(badgeColor)}, 0.4)`;

        return (
          <motion.div
            className={styles.badge}
            style={{ borderColor }}
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.2 }}
          >
            {badgeText}
          </motion.div>
        );
      })()}

      {hasAnnotation && (
        <motion.div
          className={styles.annotationIndicator}
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.2 }}
          title="This chord has an annotation"
        >
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M8.5 1.5L10.5 3.5L4 10H2V8L8.5 1.5Z"
              stroke="currentColor"
              strokeWidth="1.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </motion.div>
      )}

      {romanBelow && (
        <div className={styles.romanBelow}>{romanBelow}</div>
      )}
    </motion.div>
  );

  // Check if chord has chromatic type (GRE-114)
  const hasChromaticType = chord.isChromatic && chord.chromaticType;

  // Check if chord has low confidence (GRE-100)
  const hasLowConfidence = chord.source === 'analyzed' && chord.confidence !== undefined && chord.confidence < 0.6;

  // If chromatic, wrap in a container for badge positioning
  if (hasChromaticType) {
    return (
      <div className={styles.magicMomentWrapper}>
        {shapeContent}
        <ChromaticBadge
          chromaticType={chord.chromaticType!}
          position={{ x: 4, y: 4 }}
          isHovered={isSelected}
        />
        {/* Uncertainty badge (GRE-100) */}
        {hasLowConfidence && (
          <UncertaintyBadge
            confidence={chord.confidence!}
            position={{ x: 4, y: 4 + 24 }}
            isHovered={isSelected}
            onClick={onUncertaintyClick}
          />
        )}
      </div>
    );
  }

  // If just uncertainty (no magic moments or chromatic), wrap in a container for badge positioning
  if (hasLowConfidence) {
    return (
      <div className={styles.magicMomentWrapper}>
        {shapeContent}
        <UncertaintyBadge
          confidence={chord.confidence!}
          position={{ x: 4, y: 4 }}
          isHovered={isSelected}
          onClick={onUncertaintyClick}
        />
      </div>
    );
  }

  return shapeContent;
};

ChordShapeComponent.displayName = 'ChordShape';

export const ChordShape = React.memo(ChordShapeComponent);

/** Exported for reuse by SnippetsPanel's mini shape-strip preview. */
export function generateShapePath(scaleDegree: number, size: number): string {
  const center = size / 2;
  const radius = (size - 10) / 2;

  switch (scaleDegree) {
    case 1:
    case 6:
      return generateWobblyCircle(center, center, radius);
    case 2:
      return generateRoundedSquare(center, radius);
    case 3:
      return generateTriangle(center, radius);
    case 4:
      return generateSquare(center, radius);
    case 5:
    case 7:
      return generatePentagon(center, radius);
    default:
      return generateWobblyCircle(center, center, radius);
  }
}

function generateWobblyCircle(cx: number, cy: number, r: number): string {
  const points = 32;
  const wobbleAmount = 0.5;

  let path = '';
  for (let i = 0; i <= points; i++) {
    const angle = (i / points) * 2 * Math.PI;
    const wobbledR = r + (Math.sin(i * 5) * wobbleAmount);
    const x = cx + Math.cos(angle) * wobbledR;
    const y = cy + Math.sin(angle) * wobbledR;

    if (i === 0) {
      path += `M ${x} ${y}`;
    } else {
      path += ` L ${x} ${y}`;
    }
  }

  return path + ' Z';
}

function generateSquare(center: number, radius: number): string {
  const wobble = 1.5;
  const half = radius * 0.8;
  const w = Math.sin(Date.now() / 1000) * wobble * 0.1;

  return `M ${center - half + w} ${center - half - w} L ${center + half - w} ${center - half + w} L ${center + half + w} ${center + half - w} L ${center - half - w} ${center + half + w} Z`;
}

function generateRoundedSquare(center: number, radius: number): string {
  const half = radius * 0.8;
  const cornerRadius = 8;

  return `
    M ${center - half + cornerRadius} ${center - half}
    L ${center + half - cornerRadius} ${center - half}
    Q ${center + half} ${center - half} ${center + half} ${center - half + cornerRadius}
    L ${center + half} ${center + half - cornerRadius}
    Q ${center + half} ${center + half} ${center + half - cornerRadius} ${center + half}
    L ${center - half + cornerRadius} ${center + half}
    Q ${center - half} ${center + half} ${center - half} ${center + half - cornerRadius}
    L ${center - half} ${center - half + cornerRadius}
    Q ${center - half} ${center - half} ${center - half + cornerRadius} ${center - half}
    Z
  `;
}

function generateTriangle(center: number, radius: number): string {
  const height = radius * 1.5;

  return `M ${center} ${center - height * 0.6} L ${center + radius * 0.9} ${center + height * 0.4} L ${center - radius * 0.9} ${center + height * 0.4} Z`;
}

function generatePentagon(center: number, radius: number): string {
  const points = [];

  for (let i = 0; i < 5; i++) {
    const angle = (i / 5) * 2 * Math.PI - Math.PI / 2;
    const x = center + Math.cos(angle) * radius;
    const y = center + Math.sin(angle) * radius;
    points.push([x, y]);
  }

  return `M ${points[0][0]} ${points[0][1]} ${points.slice(1).map(p => `L ${p[0]} ${p[1]}`).join(' ')} Z`;
}

function hexToRgb(hex: string): string {
  // Handle formats like #RRGGBB or rgb(r, g, b)
  if (hex.startsWith('rgb')) {
    return hex.replace(/[^\d,]/g, '');
  }

  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) {
    return '0, 0, 0'; // Fallback
  }

  const r = parseInt(result[1], 16);
  const g = parseInt(result[2], 16);
  const b = parseInt(result[3], 16);

  return `${r}, ${g}, ${b}`;
}
